'use client';

import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { CommonCarousel } from '../../Carousel/index';
import { StatusNotification } from '../../StatusNotification/StatusNotification';

type ActionRequiredType = 'IDENTITY' | 'EMAIL' | 'PHONE';

const VERIFICATION_TRACK_MESSAGE: Partial<Record<ActionRequiredType, string>> = {
  IDENTITY: 'Identity Required',
  PHONE: 'Mobile Required',
};

type Action = {
  type: ActionRequiredType;
  label: string;
  description: string;
  link: string | null;
  notificationType: 'info' | 'warning' | 'error';
};

type ActionsRequiredSectionProps = {
  namespace: string;
  actions: Action[];
  onAction?: (action: Action) => void;
};

function ActionsRequiredSection({ namespace, actions = [], onAction }: ActionsRequiredSectionProps) {
  const router = useRouter();
  const t = useTranslations(namespace);

  const handleClick = (action: Action) => {
    if (onAction) {
      onAction(action);
      return;
    }
    if (action.link) {
      router.push(action.link);
    }
  };

  const validActions = actions.filter((action): action is Action => action != null);

  if (validActions.length === 0) {
    return null;
  }

  return (
    <CommonCarousel
      options={{
        basis: validActions.length === 1 ? 'basis-full' : 'basis-8/10',
        disableHoverEffect: true,
        fullWidth: true,
        showCount: true,
        controlsVariant: 'ghost',
      }}
      shadcnOptions={{ slidesToScroll: 1, breakpoints: {} }}
      title={<p className="paragraph text-foreground/80">{t('title')}</p>}
    >
      {validActions.map((action, index) => {
        const isInteractive = action.notificationType !== 'info';
        const trackMessage = VERIFICATION_TRACK_MESSAGE[action.type];
        return (
          <StatusNotification
            key={action.link ?? index}
            type={action.notificationType}
            label={action.label}
            description={action.description}
            {...(isInteractive && { onClick: () => handleClick(action), showChevron: true })}
            className="h-full"
            data-testid={`actions-required-${action.type.toLowerCase()}-notification`}
            {...(trackMessage && { dataTrackType: 'Verification', dataTrackMessage: trackMessage })}
          />
        );
      })}
    </CommonCarousel>
  );
}

export { ActionsRequiredSection };
